import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { fetchJson, path } from '@/api/client'
import { keys } from '@/api/keys'
import { toast } from '@/components/ui/toast'
import { Vocabulary, type Domain, type FieldDef, type KindDef, type KindDefCreate, type Semantics } from '@/schema'
import { useVocabStore, type StageDomain } from '@/stores/vocabStore'

/**
 * `/api/vocab*` (§5.2). Every mutation answers with the updated `KindDef`
 * (or a `{rev}`), and the server bumps `vocab_rev`; the client mirrors that
 * by writing the returned row into `vocabStore` and bumping its cached
 * `rev`, then invalidates the vocabulary query so the next read is exact.
 */

export interface ProposedKind {
  kind: string
  count: number
  examples: { asset_uid: string; proposal_id: number }[]
}

export async function getVocabulary(opts: { domain?: Domain; includeDisabled?: boolean } = {}, signal?: AbortSignal): Promise<Vocabulary> {
  const raw = await fetchJson<unknown>('/api/vocab', { query: { domain: opts.domain, include_disabled: opts.includeDisabled ? true : undefined }, signal })
  const parsed = Vocabulary.safeParse(raw)
  if (parsed.success) return parsed.data
  // The registry is the contract's source of truth; a row that fails to parse
  // is a server bug, but the page must still render the rows that are fine.
  console.warn('[vocab] registry failed contract validation', parsed.error.issues.slice(0, 5))
  return raw as Vocabulary
}

export function createKind(body: KindDefCreate & { meanings?: string[] }): Promise<KindDef> {
  return fetchJson<KindDef>('/api/vocab', { method: 'POST', body })
}

/** `shortcut: null` (or `''`) clears the key; `meanings` re-derives the semantics of a stage kind. */
export type KindPatch = Partial<Omit<KindDef, 'id' | 'domain' | 'origin' | 'shortcut'>> & { shortcut?: string | null; meanings?: string[] }

export function patchKind(domain: Domain, id: string, body: KindPatch): Promise<KindDef> {
  return fetchJson<KindDef>(path`/api/vocab/${domain}/${id}`, { method: 'PATCH', body })
}

export function deprecateKind(domain: Domain, id: string, aliasOf: string): Promise<KindDef> {
  return fetchJson<KindDef>(path`/api/vocab/${domain}/${id}/deprecate`, { method: 'POST', body: { alias_of: aliasOf } })
}

/** `x:foo` → `foo`; rewrites references and bumps `rev`. */
export function promoteKind(domain: Domain, id: string): Promise<{ rewritten: number; rev: number }> {
  return fetchJson(path`/api/vocab/${domain}/${id}/promote`, { method: 'POST' })
}

/** `204`, or `409 conflict` with `details.references` when the kind is in use. */
export function deleteKind(domain: Domain, id: string): Promise<void> {
  return fetchJson<void>(path`/api/vocab/${domain}/${id}`, { method: 'DELETE' })
}

export function exportVocabulary(signal?: AbortSignal): Promise<Vocabulary> {
  return fetchJson<Vocabulary>('/api/vocab/export', { signal })
}

/** Merge-by-id import of a previously exported registry. */
export function importVocabulary(file: Vocabulary | Record<string, unknown>): Promise<Vocabulary> {
  return fetchJson<Vocabulary>('/api/vocab/import', { method: 'POST', body: file })
}

export function getProposedKinds(signal?: AbortSignal): Promise<{ items: ProposedKind[] }> {
  return fetchJson('/api/vocab/proposed', { signal })
}

/* ------------------------------------------- the annotator's plain-language kinds --- */

/** One plain-language choice ("Walk on", "Sit on", "Through a door") for a stage domain. */
export interface MeaningDef {
  id: string
  label: string
  hint: string
  /** Kebab-case lucide name. */
  icon: string
  /** Choices sharing a group are exclusive (pick at most one). */
  group: string | null
}

export interface DomainMeanings {
  noun: { one: string; many: string }
  default_icon: string
  meanings: MeaningDef[]
  exclusive_groups: Record<string, string[]>
  blank_semantics: Record<string, unknown>
  role_z?: Record<string, number>
}

export interface MeaningCatalogue {
  domains: Partial<Record<StageDomain, DomainMeanings>>
}

export interface ResolvedMeanings {
  domain: StageDomain
  meanings: string[]
  semantics: Semantics
  /** Plain sentences: what the kind does in stories. */
  summary: string[]
}

export interface KindSuggestion {
  domain: StageDomain
  id: string
  label: Record<string, string>
  color: string
  icon: string
  /** The built-in kind the name was read as, if any. */
  like: string | null
  meanings: string[]
  semantics: Semantics
  summary: string[]
  /** A kind that already has this name. */
  existing: string | null
}

/** Body of `POST /api/vocab/quick`: a stage kind from a name and plain-language choices. */
export interface QuickKindBody {
  domain: StageDomain
  name: string
  id?: string
  /** `[]` = deliberately does nothing; omitted = guessed from the name. */
  meanings?: string[]
  semantics?: Record<string, unknown>
  like?: string
  description?: string
  ai_hint?: string
  color?: string
  icon?: string
  shortcut?: string
  fields?: FieldDef[]
  enabled?: boolean
}

export interface KindDetail {
  rev: number
  kind: KindDef
  resolved_id: string
  summary: string[]
  meanings?: string[]
  usage: { count: number; assets: string[] }
}

/** `GET /api/vocab/meanings` — the choices per stage domain (static on the server). */
export function getMeanings(domain?: StageDomain, signal?: AbortSignal): Promise<MeaningCatalogue> {
  return fetchJson<MeaningCatalogue>('/api/vocab/meanings', { query: { domain }, signal })
}

/** Choices → the semantics they mean plus the sentences explaining them. Saves nothing. */
export function resolveMeanings(body: { domain: StageDomain; meanings: string[]; base?: Record<string, unknown> }, signal?: AbortSignal): Promise<ResolvedMeanings> {
  return fetchJson<ResolvedMeanings>('/api/vocab/meanings/resolve', { method: 'POST', body, signal })
}

/** A name → a free id, a colour, an icon and the meaning the name most likely has. */
export function suggestKind(body: { domain: StageDomain; name: string }, signal?: AbortSignal): Promise<KindSuggestion> {
  return fetchJson<KindSuggestion>('/api/vocab/suggest', { method: 'POST', body, signal })
}

/** Create a stage kind in one call (the annotator's "+ New kind"). */
export function quickCreateKind(body: QuickKindBody): Promise<KindDef> {
  return fetchJson<KindDef>('/api/vocab/quick', { method: 'POST', body })
}

export function getKindDetail(domain: Domain, id: string, signal?: AbortSignal): Promise<KindDetail> {
  return fetchJson<KindDetail>(path`/api/vocab/${domain}/${id}`, { signal })
}

export interface InferResult {
  dry_run: boolean
  rev: number | null
  changed: { domain: Domain; id: string; reason: 'guessed' | 'z_order'; semantics: Semantics; like?: string }[]
  skipped: string[]
}

/** Give the do-nothing shape kinds (from picture files) the meaning their name suggests. Never touches builtins or kinds a person made. */
export function inferKinds(dryRun = false): Promise<InferResult> {
  return fetchJson<InferResult>('/api/vocab/infer', { method: 'POST', body: { dry_run: dryRun } })
}

/** Put `ids` first, in that order; the rest keep their relative order. */
export function reorderKinds(domain: Domain, ids: string[]): Promise<{ rev: number; domain: Domain; ids: string[] }> {
  return fetchJson(path`/api/vocab/${domain}/reorder`, { method: 'POST', body: { ids } })
}

/* ------------------------------------------------------------- queries --- */

export const vocabularyQuery = (opts: { domain?: Domain; includeDisabled?: boolean } = {}) =>
  queryOptions({
    queryKey: keys.vocab.list(opts.domain, opts.includeDisabled ?? false),
    queryFn: ({ signal }) => getVocabulary(opts, signal),
    staleTime: 60_000,
    // The app turns focus refetching off globally; the kinds list opts back in, so kinds added in
    // another tab or by someone else show up when you come back (at most once a minute).
    refetchOnWindowFocus: true,
  })

export const meaningsQuery = (domain?: StageDomain) =>
  queryOptions({
    queryKey: ['vocab-meanings', domain ?? 'all'] as const,
    queryFn: ({ signal }) => getMeanings(domain, signal),
    staleTime: Infinity,
    retry: 1,
  })

export const kindDetailQuery = (domain: Domain, id: string) =>
  queryOptions({
    queryKey: [...keys.vocab.all(), 'detail', domain, id] as const,
    queryFn: ({ signal }) => getKindDetail(domain, id, signal),
    staleTime: 10_000,
  })

export const proposedKindsQuery = queryOptions({
  queryKey: keys.vocab.proposed(),
  queryFn: ({ signal }) => getProposedKinds(signal),
  staleTime: 30_000,
  retry: 1,
})

/** Load the whole registry (deprecated rows included) and mirror it into `vocabStore`. */
export function useVocabulary(opts: { includeDisabled?: boolean } = {}) {
  const query = useQuery(vocabularyQuery({ includeDisabled: opts.includeDisabled ?? true }))
  const setVocabulary = useVocabStore((s) => s.setVocabulary)
  useEffect(() => {
    if (query.data) setVocabulary(query.data)
  }, [query.data, setVocabulary])
  return query
}

export function invalidateVocabulary(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: keys.vocab.all() })
}

function describe(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

/**
 * The five kind mutations, each writing the result into the store
 * (bumping the cached `rev`) and invalidating the query.
 */
export function useKindMutations() {
  const qc = useQueryClient()
  const store = useVocabStore.getState

  const create = useMutation({
    mutationFn: (body: KindDefCreate & { meanings?: string[] }) => createKind(body),
    onSuccess: (kind) => {
      store().upsertKind(kind, { bumpRev: true })
      toast.success(`Added ${kind.label['en'] ?? kind.id}`)
      void invalidateVocabulary(qc)
    },
    onError: (e) => toast.error('Could not add the kind', { description: describe(e) }),
  })

  const patch = useMutation({
    mutationFn: ({ domain, id, body }: { domain: Domain; id: string; body: KindPatch }) => patchKind(domain, id, body),
    onSuccess: (kind) => {
      store().upsertKind(kind, { bumpRev: true })
      void invalidateVocabulary(qc)
    },
    onError: (e) => toast.error('Could not save the kind', { description: describe(e) }),
  })

  const deprecate = useMutation({
    mutationFn: ({ domain, id, aliasOf }: { domain: Domain; id: string; aliasOf: string }) => deprecateKind(domain, id, aliasOf),
    onSuccess: (kind) => {
      store().upsertKind(kind, { bumpRev: true })
      toast.success(`Deprecated ${kind.id}`, { description: kind.alias_of ? `Readers now resolve it to ${kind.alias_of}.` : undefined })
      void invalidateVocabulary(qc)
    },
    onError: (e) => toast.error('Could not deprecate', { description: describe(e) }),
  })

  const promote = useMutation({
    mutationFn: ({ domain, id }: { domain: Domain; id: string }) => promoteKind(domain, id),
    onSuccess: ({ rewritten, rev }) => {
      store().setRev(rev)
      toast.success('Promoted to a built-in id', { description: `${rewritten} reference${rewritten === 1 ? '' : 's'} rewritten.` })
      void invalidateVocabulary(qc)
      void qc.invalidateQueries({ queryKey: keys.annotation.all() })
    },
    onError: (e) => toast.error('Could not promote', { description: describe(e) }),
  })

  const quick = useMutation({
    mutationFn: (body: QuickKindBody) => quickCreateKind(body),
    onSuccess: (kind) => {
      store().upsertKind(kind, { bumpRev: true })
      toast.success(`Added ${kind.label['en'] ?? kind.id}`)
      void invalidateVocabulary(qc)
    },
    onError: (e) => toast.error('Could not add the kind', { description: describe(e) }),
  })

  const reorder = useMutation({
    mutationFn: ({ domain, ids }: { domain: Domain; ids: string[] }) => reorderKinds(domain, ids),
    onSuccess: ({ rev }) => {
      store().setRev(rev)
      void invalidateVocabulary(qc)
    },
    onError: (e) => toast.error('Could not change the order', { description: describe(e) }),
  })

  const infer = useMutation({
    mutationFn: () => inferKinds(false),
    onSuccess: (res) => {
      if (res.rev != null) store().setRev(res.rev)
      toast.success(res.changed.length ? `Set up ${res.changed.length} kind${res.changed.length === 1 ? '' : 's'}` : 'Nothing needed setting up')
      void invalidateVocabulary(qc)
    },
    onError: (e) => toast.error('Could not set the kinds up', { description: describe(e) }),
  })

  const remove = useMutation({
    mutationFn: ({ domain, id }: { domain: Domain; id: string }) => deleteKind(domain, id),
    onSuccess: (_, { domain, id }) => {
      store().removeKind(domain, id)
      toast.success(`Deleted ${id}`)
      void invalidateVocabulary(qc)
    },
  })

  return { create, patch, deprecate, promote, remove, quick, reorder, infer }
}
